import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api, { apiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { fmtWind } from '@/lib/units';
import { Bell, BellRing, BellOff, Loader2, Trash2, MoonStar } from 'lucide-react';
import { toast } from 'sonner';

export const WindAlertCard = ({ spot, units, user }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [alert, setAlert] = useState(null);
  const [threshold, setThreshold] = useState(15); // knots (canonical)
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    try {
      const { data } = await api.get('/wind-alerts');
      const mine = (data || []).find((a) => a.spot_id === spot.id) || null;
      setAlert(mine);
      if (mine) setThreshold(Math.round(mine.min_wind_knots));
    } catch (e) {
      // Non-blocking: a failed lookup just shows the "set alert" state.
      console.warn('[WindAlertCard] failed to load wind alert', e);
    } finally {
      setLoading(false);
    }
  }, [user, spot.id]);

  useEffect(() => { load(); }, [load]);

  const save = async (activeFlag = true) => {
    setSaving(true);
    try {
      const { data } = await api.post('/wind-alerts', {
        spot_id: spot.id,
        min_wind_knots: threshold,
        active: activeFlag,
      });
      setAlert(data);
      toast.success(activeFlag ? t('windAlert.toast.saved') : t('windAlert.toast.paused'));
    } catch (e) {
      toast.error(apiError(e, t('windAlert.toast.error')));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (next) => {
    if (!alert) return save(next);
    setSaving(true);
    try {
      const { data } = await api.put(`/wind-alerts/${alert.id}`, {
        spot_id: spot.id,
        min_wind_knots: threshold,
        active: next,
      });
      setAlert(data);
      toast.success(next ? t('windAlert.toast.resumed') : t('windAlert.toast.paused'));
    } catch (e) {
      toast.error(apiError(e, t('windAlert.toast.error')));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!alert) return;
    setSaving(true);
    try {
      await api.delete(`/wind-alerts/${alert.id}`);
      setAlert(null);
      toast.success(t('windAlert.toast.removed'));
    } catch (e) {
      toast.error(apiError(e, t('windAlert.toast.error')));
    } finally {
      setSaving(false);
    }
  };

  const Icon = alert && alert.active ? BellRing : Bell;
  const accent = alert && alert.active ? '#2EE59D' : '#00B4D8';

  return (
    <div className="kite-card p-5" data-testid="wind-alert-card">
      <div className="flex items-center justify-between gap-3 mb-1">
        <h2 className="font-semibold flex items-center gap-2">
          <Icon className="w-4 h-4" style={{ color: accent }} /> {t('windAlert.title')}
        </h2>
        {alert && (
          <span className="flex items-center gap-2" data-testid="wind-alert-active-toggle">
            <span className="text-xs text-muted-foreground">{alert.active ? t('windAlert.on') : t('windAlert.off')}</span>
            <Switch checked={alert.active} onCheckedChange={toggleActive} disabled={saving} />
          </span>
        )}
      </div>
      <p className="text-sm text-muted-foreground">{t('windAlert.subtitle')}</p>

      {!user ? (
        <Button
          onClick={() => navigate('/login')}
          className="w-full h-11 mt-4 bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold rounded-xl"
          data-testid="wind-alert-login-btn"
        >
          {t('windAlert.loginToSet')}
        </Button>
      ) : loading ? (
        <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#00B4D8]" /></div>
      ) : (
        <>
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="label-eyebrow text-muted-foreground">{t('windAlert.thresholdLabel')}</span>
              <span className="font-data text-lg font-semibold text-[#48CAE4]" data-testid="wind-alert-threshold-value">
                {fmtWind(threshold, units)}+
              </span>
            </div>
            <Slider
              value={[threshold]}
              onValueChange={(v) => setThreshold(v[0])}
              min={8}
              max={40}
              step={1}
              className="py-1"
              data-testid="wind-alert-slider"
            />
            <div className="flex justify-between text-[11px] text-muted-foreground mt-1 font-data">
              <span>{fmtWind(8, units)}</span>
              <span>{fmtWind(40, units)}</span>
            </div>
          </div>

          <div className="flex items-center gap-2 mt-4">
            <Button
              onClick={() => save(true)}
              disabled={saving}
              className="flex-1 h-11 bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold rounded-xl"
              data-testid="wind-alert-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <>{alert ? <BellRing className="w-4 h-4 mr-2" /> : <Bell className="w-4 h-4 mr-2" />}
                  {alert ? t('windAlert.update') : t('windAlert.setBtn')}</>
              )}
            </Button>
            {alert && (
              <Button
                variant="outline"
                onClick={remove}
                disabled={saving}
                className="h-11 border-white/15 bg-white/5 hover:bg-[#FF6B35]/15 hover:text-[#FF6B35] rounded-xl"
                data-testid="wind-alert-remove-btn"
                aria-label={t('windAlert.remove')}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>

          <p className="text-[11px] text-muted-foreground mt-3 flex items-start gap-1.5">
            <MoonStar className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {t('windAlert.quietHint')}
          </p>
        </>
      )}
    </div>
  );
};
